import ZariDivider from "../components/ZariDivider";

function Returns() {
    return (
        <>
            <title>Refund and Returns Policy | Fashion Freude</title>
            <meta name="title" content="Refund and Returns Policy | Fashion Freude" />
            <meta
                name="description"
                content="Read the Refund and Returns Policy for Fashion Freude. Learn about our 30-day return window, eligibility requirements, non-returnable items, and refund process."
            />
            <meta
                name="keywords"
                content="Fashion Freude return policy, refund policy, 30 day return, ethnic wear returns, Fashion Freude refunds"
            />
            <meta name="robots" content="index, follow" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <link rel="canonical" href="https://fashionfreude.com/returns-and-refunds" />

            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://fashionfreude.com/returns-and-refunds" />
            <meta property="og:title" content="Refund and Returns Policy | Fashion Freude" />
            <meta
                property="og:description"
                content="Learn about our 30-day return policy, eligibility criteria, non-returnable ethnic wear items, and refund processing details at Fashion Freude."
            />
            <meta property="og:image" content="https://fashionfreude.com/logo.png" />

            <meta name="twitter:card" content="summary" />
            <meta name="twitter:url" content="https://fashionfreude.com/returns-and-refunds" />
            <meta name="twitter:title" content="Refund and Returns Policy | Fashion Freude" />
            <meta
                name="twitter:description"
                content="Learn about our 30-day return policy, eligibility criteria, non-returnable ethnic wear items, and refund processing details at Fashion Freude."
            />
            <meta name="twitter:image" content="https://fashionfreude.com/logo.png" />

            <div className="bg-ivory">
                <section className="bg-cream">
                    <div className="container-luxe py-16 text-center">
                        <p className="eyebrow">Shop With Confidence</p>

                        <h1 className="mt-3 font-display text-4xl leading-tight text-ink sm:text-5xl">
                            Refund and Returns Policy
                        </h1>

                        <ZariDivider className="mt-5" />
                    </div>
                </section>

                {/* ==========================================
                RETURNS & REFUND POLICY
            ========================================== */}

                <section className="container-luxe py-14">
                    <div className="mx-auto max-w-3xl">
                        <div className="card p-8 space-y-6 text-muted leading-relaxed">
                            <h2 className="font-display text-2xl text-ink">Overview</h2>

                            <p>
                                Our return and refund policy is valid for <strong className="text-ink">30 days</strong>
                                from the date of purchase. If more than 30 days have passed,
                                unfortunately we cannot offer a full refund or exchange.
                            </p>

                            <h3 className="font-display text-2xl text-ink">To be eligible for a return:</h3>

                            <ul className="list-disc space-y-2 pl-6 marker:text-gold">
                                <li>
                                    The product must be unused, unwashed, and in the same condition
                                    as you received it.
                                </li>

                                <li>
                                    It must be returned in the original packaging, with tags intact.
                                </li>

                                <li>A valid receipt or proof of purchase is required.</li>
                            </ul>

                            <h3 className="font-display text-2xl text-ink">Non-Returnable Items</h3>

                            <p>
                                Due to hygiene and custom design considerations, the following items
                                are non-returnable:
                            </p>

                            <ul className="list-disc space-y-2 pl-6 marker:text-gold">
                                <li>
                                    kurtis, lehenga-cholis that have been altered, custom-stitched,
                                    or used.
                                </li>

                                <li>Items marked as final sale or discounted.</li>

                                <li>
                                    Innerwear, petticoats, or blouse pieces (if sold separately).
                                </li>

                                <li>Gift cards or downloadable products (if applicable).</li>
                            </ul>

                            <h3 className="font-display text-2xl text-ink">Partial refunds may be granted if:</h3>

                            <ul className="list-disc space-y-2 pl-6 marker:text-gold">
                                <li>
                                    The item shows signs of use (e.g., perfume stains, makeup
                                    marks).
                                </li>

                                <li>The item is damaged or missing parts not due to our error.</li>

                                <li>The return is requested after 30 days of delivery.</li>
                            </ul>

                            <h3 className="font-display text-2xl text-ink">Refund</h3>

                            <p>
                                Once your returned item is received and inspected, we will notify
                                you via email about the approval or rejection of your refund.
                                <br />
                                <br />
                                If approved, your refund will be processed and credited back to your
                                original method of payment within 7–10 business days.
                            </p>

                            <h3 className="font-display text-2xl text-ink">Late or Missing Refunds</h3>

                            <ol className="list-decimal space-y-2 pl-6 marker:text-gold">
                                <li>Check your bank account again.</li>

                                <li>
                                    Contact your credit card company (it may take some time to
                                    reflect).
                                </li>

                                <li>Contact your bank (processing times may vary).</li>
                            </ol>

                            <h3 className="font-display text-2xl text-ink">Sale Items</h3>

                            <p>
                                Only regular-priced items may be refunded. Items purchased on sale
                                or promotion are non-refundable.
                            </p>

                            <h3 className="font-display text-2xl text-ink">Gifts</h3>

                            <p>
                                If the item was marked as a gift and shipped directly to you, you’ll
                                receive a gift credit for the value of your return. Once we receive
                                the item, a gift voucher will be emailed or mailed to you.
                            </p>

                            <h3 className="font-display text-2xl text-ink">Return Shipping</h3>

                            <ul className="list-disc space-y-2 pl-6 marker:text-gold">
                                <li>Customers are responsible for return shipping costs.</li>

                                <li>
                                    Shipping costs are non-refundable. If a refund is issued, the
                                    return shipping cost may be deducted.
                                </li>

                                <li>
                                    For valuable returns, consider using a trackable shipping
                                    service or shipping insurance. We do not guarantee receipt of
                                    returned items.
                                </li>
                            </ul>

                            <div className="rounded-2xl border border-sand bg-cream p-6">
                                <h3 className="font-display text-2xl text-ink">Need Help?</h3>

                                <p className="mt-3">
                                    For questions about refunds or returns, contact us at our
                                    Contact Us Page.
                                </p>

                                <p className="mt-2">
                                    <strong className="text-ink">Email:</strong> fashionfreude@gmail.com
                                </p>

                                <p className="mt-2">
                                    <strong className="text-ink">Phone:</strong> +91 98765 43210
                                </p>

                                <p className="mt-2">
                                    <strong className="text-ink">Address:</strong> 1802 Millennium Heights, Nana Mava
                                    Road, Rajkot, Gujarat - 360004
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}

export default Returns;
